import React from 'react';
import {
  QRPayload,
  SocialPlatform,
  CryptoCoin
} from '@/types/qr';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ValidationError } from '@/lib/qr/validation';

interface ContentEditorProps {
  payload: QRPayload;
  onChange: (payload: QRPayload) => void;
  errors: ValidationError[];
}

export const ContentEditor: React.FC<ContentEditorProps> = ({
  payload,
  onChange,
  errors
}) => {
  const getFieldError = (field: string) => errors.find((e) => e.field === field)?.message;

  switch (payload.type) {
    case 'url':
      return (
        <div className="space-y-4">
          <Input
            label="Website URL"
            type="url"
            inputMode="url"
            placeholder="https://qr.planmaster.in"
            value={payload.url}
            error={getFieldError('url')}
            onChange={(e) => onChange({ ...payload, url: e.target.value })}
            helperText="Include https:// for standard web links"
          />
          <Input
            label="Label / Title (Optional)"
            placeholder="My Portfolio or Business"
            value={payload.title || ''}
            onChange={(e) => onChange({ ...payload, title: e.target.value })}
          />
        </div>
      );

    case 'text':
      return (
        <div className="space-y-2">
          <label className="block text-[12px] font-normal tracking-tight text-muted-foreground">
            Plain Text Content
          </label>
          <textarea
            rows={5}
            className="w-full rounded-xl bg-card border border-input p-3.5 text-[17px] text-foreground placeholder-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20 focus:outline-none transition-all tracking-tight"
            placeholder="Enter any text, code snippet, notes, or instructions..."
            value={payload.text}
            onChange={(e) => onChange({ ...payload, text: e.target.value })}
          />
          <div className="flex justify-between items-center text-[12px] text-muted-foreground">
            <span>{payload.text.length} characters</span>
            {getFieldError('text') && (
              <span className="text-destructive font-medium">{getFieldError('text')}</span>
            )}
          </div>
        </div>
      );

    case 'wifi':
      return (
        <div className="space-y-4">
          <Input
            label="Network Name (SSID)"
            placeholder="e.g. StudioGuest-5G"
            value={payload.ssid}
            error={getFieldError('ssid')}
            onChange={(e) => onChange({ ...payload, ssid: e.target.value })}
          />
          <Input
            label="Wi-Fi Password"
            type="password"
            placeholder="Enter Wi-Fi network password"
            value={payload.password || ''}
            error={getFieldError('password')}
            onChange={(e) => onChange({ ...payload, password: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select
              label="Security Encryption"
              value={payload.encryption}
              onChange={(e) =>
                onChange({
                  ...payload,
                  encryption: e.target.value as 'WPA' | 'WEP' | 'nopass'
                })
              }
              options={[
                { value: 'WPA', label: 'WPA / WPA2 / WPA3 (Standard)' },
                { value: 'WEP', label: 'WEP (Legacy)' },
                { value: 'nopass', label: 'No Password (Open)' }
              ]}
            />
            <div className="flex items-center sm:pt-6">
              <label className="flex items-center gap-2 text-[14px] text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={payload.hidden || false}
                  onChange={(e) => onChange({ ...payload, hidden: e.target.checked })}
                  className="rounded border-input text-primary focus:ring-ring h-4 w-4"
                />
                <span>Hidden Network</span>
              </label>
            </div>
          </div>
        </div>
      );

    case 'vcard':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First Name"
              placeholder="Jane"
              value={payload.firstName}
              error={getFieldError('firstName')}
              onChange={(e) => onChange({ ...payload, firstName: e.target.value })}
            />
            <Input
              label="Last Name"
              placeholder="Doe"
              value={payload.lastName || ''}
              onChange={(e) => onChange({ ...payload, lastName: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Organization / Company"
              placeholder="Acme Corp"
              value={payload.organization || ''}
              onChange={(e) => onChange({ ...payload, organization: e.target.value })}
            />
            <Input
              label="Job Title"
              placeholder="Product Designer"
              value={payload.title || ''}
              onChange={(e) => onChange({ ...payload, title: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Mobile Phone"
              type="tel"
              inputMode="tel"
              placeholder="+1 (555) 000-0000"
              value={payload.phone || ''}
              onChange={(e) => onChange({ ...payload, phone: e.target.value })}
            />
            <Input
              label="Email Address"
              type="email"
              inputMode="email"
              placeholder="jane@example.com"
              value={payload.email || ''}
              error={getFieldError('email')}
              onChange={(e) => onChange({ ...payload, email: e.target.value })}
            />
          </div>
          <Input
            label="Website"
            type="url"
            placeholder="https://janedoe.com"
            value={payload.website || ''}
            onChange={(e) => onChange({ ...payload, website: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="City"
              placeholder="San Francisco"
              value={payload.city || ''}
              onChange={(e) => onChange({ ...payload, city: e.target.value })}
            />
            <Input
              label="State / Province"
              placeholder="CA"
              value={payload.state || ''}
              onChange={(e) => onChange({ ...payload, state: e.target.value })}
            />
            <Input
              label="Country"
              placeholder="USA"
              value={payload.country || ''}
              onChange={(e) => onChange({ ...payload, country: e.target.value })}
            />
          </div>
        </div>
      );

    case 'upi':
      return (
        <div className="space-y-4">
          <Input
            label="Payee UPI ID (VPA)"
            placeholder="merchant@okhdfcbank"
            value={payload.vpa}
            error={getFieldError('vpa')}
            onChange={(e) => onChange({ ...payload, vpa: e.target.value })}
            helperText="e.g. name@upi, user@paytm, business@okaxis"
          />
          <Input
            label="Payee Business / Person Name"
            placeholder="Acme Stores"
            value={payload.payeeName}
            error={getFieldError('payeeName')}
            onChange={(e) => onChange({ ...payload, payeeName: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Amount (Optional, ₹)"
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={payload.amount || ''}
              error={getFieldError('amount')}
              onChange={(e) => onChange({ ...payload, amount: e.target.value })}
            />
            <Input
              label="Transaction Note"
              placeholder="Bill Payment / Invoice"
              value={payload.transactionNote || ''}
              onChange={(e) => onChange({ ...payload, transactionNote: e.target.value })}
            />
          </div>
        </div>
      );

    case 'email':
      return (
        <div className="space-y-4">
          <Input
            label="Recipient Email"
            type="email"
            placeholder="support@company.com"
            value={payload.email}
            error={getFieldError('email')}
            onChange={(e) => onChange({ ...payload, email: e.target.value })}
          />
          <Input
            label="Subject (Optional)"
            placeholder="Inquiry regarding services"
            value={payload.subject || ''}
            onChange={(e) => onChange({ ...payload, subject: e.target.value })}
          />
          <div>
            <label className="block text-[12px] font-normal tracking-tight text-muted-foreground mb-1.5">
              Email Body (Optional)
            </label>
            <textarea
              rows={3}
              className="w-full rounded-xl bg-card border border-input p-3 text-[17px] text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none tracking-tight"
              placeholder="Pre-fill message text..."
              value={payload.body || ''}
              onChange={(e) => onChange({ ...payload, body: e.target.value })}
            />
          </div>
        </div>
      );

    case 'phone':
      return (
        <div className="space-y-4">
          <Input
            label="Phone Number"
            type="tel"
            inputMode="tel"
            placeholder="+1 555-0199"
            value={payload.phone}
            error={getFieldError('phone')}
            onChange={(e) => onChange({ ...payload, phone: e.target.value })}
            helperText="Include country code for universal international dialing"
          />
        </div>
      );

    case 'sms':
      return (
        <div className="space-y-4">
          <Input
            label="Phone Number"
            type="tel"
            inputMode="tel"
            placeholder="+1 555-0199"
            value={payload.phone}
            error={getFieldError('phone')}
            onChange={(e) => onChange({ ...payload, phone: e.target.value })}
          />
          <div>
            <label className="block text-[12px] font-normal tracking-tight text-muted-foreground mb-1.5">
              SMS Message Text
            </label>
            <textarea
              rows={3}
              className="w-full rounded-xl bg-card border border-input p-3 text-[17px] text-foreground placeholder-muted-foreground focus:border-ring focus:outline-none tracking-tight"
              placeholder="Pre-written message body..."
              value={payload.message || ''}
              onChange={(e) => onChange({ ...payload, message: e.target.value })}
            />
          </div>
        </div>
      );

    case 'geo':
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Latitude"
              type="number"
              inputMode="decimal"
              placeholder="37.7749"
              value={payload.latitude}
              error={getFieldError('latitude')}
              onChange={(e) => onChange({ ...payload, latitude: e.target.value })}
            />
            <Input
              label="Longitude"
              type="number"
              inputMode="decimal"
              placeholder="-122.4194"
              value={payload.longitude}
              error={getFieldError('longitude')}
              onChange={(e) => onChange({ ...payload, longitude: e.target.value })}
            />
          </div>
          <Input
            label="Location Query / Name (Optional)"
            placeholder="Golden Gate Bridge, San Francisco"
            value={payload.query || ''}
            onChange={(e) => onChange({ ...payload, query: e.target.value })}
          />
        </div>
      );

    case 'event':
      return (
        <div className="space-y-4">
          <Input
            label="Event Title"
            placeholder="Product Launch 2026"
            value={payload.title}
            error={getFieldError('title')}
            onChange={(e) => onChange({ ...payload, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Start Date & Time"
              type="datetime-local"
              value={payload.startDate ? payload.startDate.slice(0, 16) : ''}
              error={getFieldError('startDate')}
              onChange={(e) => onChange({ ...payload, startDate: new Date(e.target.value).toISOString() })}
            />
            <Input
              label="End Date & Time"
              type="datetime-local"
              value={payload.endDate ? payload.endDate.slice(0, 16) : ''}
              onChange={(e) => onChange({ ...payload, endDate: new Date(e.target.value).toISOString() })}
            />
          </div>
          <Input
            label="Event Location"
            placeholder="Auditorium / Online Zoom"
            value={payload.location || ''}
            onChange={(e) => onChange({ ...payload, location: e.target.value })}
          />
        </div>
      );

    case 'social':
      return (
        <div className="space-y-4">
          <Select
            label="Social Platform"
            value={payload.platform}
            onChange={(e) => onChange({ ...payload, platform: e.target.value as SocialPlatform })}
            options={[
              { value: 'instagram', label: 'Instagram' },
              { value: 'x', label: 'X (Twitter)' },
              { value: 'facebook', label: 'Facebook' },
              { value: 'linkedin', label: 'LinkedIn' },
              { value: 'youtube', label: 'YouTube' },
              { value: 'github', label: 'GitHub' },
              { value: 'tiktok', label: 'TikTok' },
              { value: 'whatsapp', label: 'WhatsApp' },
              { value: 'telegram', label: 'Telegram' }
            ]}
          />
          <Input
            label="Username or Profile URL"
            placeholder="username or https://..."
            value={payload.usernameOrUrl}
            error={getFieldError('usernameOrUrl')}
            onChange={(e) => onChange({ ...payload, usernameOrUrl: e.target.value })}
          />
        </div>
      );

    case 'app':
      return (
        <div className="space-y-4">
          <Input
            label="iOS App Store URL"
            type="url"
            placeholder="https://apps.apple.com/app/..."
            value={payload.iosUrl || ''}
            onChange={(e) => onChange({ ...payload, iosUrl: e.target.value })}
          />
          <Input
            label="Google Play Store URL"
            type="url"
            placeholder="https://play.google.com/store/apps/..."
            value={payload.androidUrl || ''}
            onChange={(e) => onChange({ ...payload, androidUrl: e.target.value })}
          />
          <Input
            label="Fallback Website Landing Page"
            type="url"
            placeholder="https://myapp.com/download"
            value={payload.fallbackUrl || ''}
            onChange={(e) => onChange({ ...payload, fallbackUrl: e.target.value })}
          />
        </div>
      );

    case 'crypto':
      return (
        <div className="space-y-4">
          <Select
            label="Cryptocurrency"
            value={payload.coin}
            onChange={(e) => onChange({ ...payload, coin: e.target.value as CryptoCoin })}
            options={[
              { value: 'BTC', label: 'Bitcoin (BTC)' },
              { value: 'ETH', label: 'Ethereum (ETH)' },
              { value: 'SOL', label: 'Solana (SOL)' },
              { value: 'USDT', label: 'Tether (USDT)' },
              { value: 'DOGE', label: 'Dogecoin (DOGE)' }
            ]}
          />
          <Input
            label="Wallet Public Address"
            placeholder="Enter public recipient address"
            value={payload.address}
            error={getFieldError('address')}
            onChange={(e) => onChange({ ...payload, address: e.target.value })}
          />
          <Input
            label="Requested Amount (Optional)"
            type="number"
            inputMode="decimal"
            placeholder="0.05"
            value={payload.amount || ''}
            onChange={(e) => onChange({ ...payload, amount: e.target.value })}
          />
        </div>
      );

    case 'custom':
      return (
        <div className="space-y-4">
          <Input
            label="Custom URI / Data Payload"
            placeholder="custom-app://open?token=xyz"
            value={payload.content}
            error={getFieldError('content')}
            onChange={(e) => onChange({ ...payload, content: e.target.value })}
          />
        </div>
      );

    default:
      return null;
  }
};
